import React from "react";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button, Form, Radio } from "semantic-ui-react";
import Store from "domain/model/Store";

type FormValues = {
  title: string;
  body: string;
  active: boolean;
};

const validationSchema = Yup.object().shape({
  title: Yup.string().required(),
  body: Yup.string().required(),
  active: Yup.boolean(),
});

type Props = {
  initialValues: FormValues;
  currentStore: Store;
  handleSubmit: ({
    values,
    resetForm,
  }: {
    values: FormValues;
    resetForm: () => void;
  }) => void;
};

const TakeoutMessageTemplateForm: React.FC<Props> = ({
  initialValues,
  handleSubmit,
}) => {
  const formik = useFormik({
    initialValues,
    onSubmit: (values, { resetForm }) => {
      handleSubmit({ values, resetForm });
    },
    initialStatus: { success: "", error: "" },
    validationSchema: validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
  });

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Form.Field required>
        <label>タイトル</label>
        <Form.Input
          name="title"
          error={formik.errors.title}
          value={formik.values.title}
          onChange={formik.handleChange}
        />
      </Form.Field>
      <Form.Field required>
        <label>本文</label>
        <Form.TextArea
          name="body"
          error={formik.errors.body}
          value={formik.values.body}
          onChange={formik.handleChange}
          rows={25}
        />
        <FormHelper>
          メール本文中で「<b>&lt;%= @注文者名%&gt;</b>」
          などの形で注文情報から展開可能な項目は、以下のとおりです。
          <br />
          <ul>
            <li>@店舗名</li>
            <li>@店舗電話番号</li>
            <li>@注文者名</li>
            <li>@注文電話番号</li>
            <li>@注文メールアドレス</li>
            <li>@注文者郵便番号</li>
            <li>@注文者住所</li>
            <li>@注文番号</li>
            <li>@注文金額</li>
            <li>@合計金額 ※配送料を含みます</li>
            <li>@注文日時</li>
            <li>@受取予定日時</li>
            <li>@注文明細</li>
            <li>@決済方法</li>
            <li>@配送先氏名 ※指定がなかった場合は注文者名と同じです</li>
            <li>@配送先電話番号 ※指定がなかった場合は注文電話番号と同じです</li>
            <li>
              @配送先郵便番号 ※指定がなかった場合は注文者郵便番号と同じです
            </li>
            <li>@配送先住所 ※指定がなかった場合は注文者住所と同じです</li>
            <li>@配送料</li>
            <li>@備考</li>
          </ul>
        </FormHelper>
      </Form.Field>

      <ToggleWrapper>
        <ToggleLabel>無効</ToggleLabel>
        <Radio
          toggle
          css={{ margin: "0 0.5em" }}
          id="active"
          name="active"
          error={formik.errors.active}
          checked={formik.values.active}
          onClick={() => {
            formik.setFieldValue("active", !formik.values.active);
          }}
        />
        <ToggleLabel>有効</ToggleLabel>
      </ToggleWrapper>

      <div style={{ textAlign: "center" }}>
        <Button color="blue" content="保存" type="submit" />
      </div>
    </Form>
  );
};

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 2rem 0;
`;

const ToggleLabel = styled.div`
  font-weight: bolder;
`;

const FormHelper = styled.div`
  color: #888;
  font-size: 0.9rem;
`;

export default TakeoutMessageTemplateForm;
